import { useContext, useRef } from 'react'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import Layer from '.'
import { CreationToolsArtboardContext } from './../context'
import { ArtboardBaseLayerProps, ArtboardDragPosition, ArtboardTextProps } from '../types'
import { generateCaptionCanvas } from '../utils'
import usePlayheadTime from '../../source-editor/hooks/use-playeadTime'

interface Props extends ArtboardBaseLayerProps, ArtboardTextProps {
    maxWidth: number
    forceSelected?: boolean
    onMove?: (position: ArtboardDragPosition) => void
}

const CreationToolsArtboardTextLayer = ({
    caption,
    defaultX,
    defaultY,
    defaultRotation = 0,
    forceSelected,
    resizeable,
    rotateable,
    moveable,
    text,
    color,
    strokeColor,
    fontFamily,
    fontSize,
    fontWeight = 'normal',
    lineHeight = 1.3,
    maxWidth,
    onMove,
}: Props) => {
    const { canvasScale, draggingCaptionId, contextMenuCaptionId } = useContext(CreationToolsArtboardContext)
    const { currentPlayheadTime } = usePlayheadTime()
    const canvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'))

    useAsyncEffect(async () => {
        if (!canvasRef.current) return

        // draw caption to canvas
        await generateCaptionCanvas(
            {
                text,
                color,
                strokeColor,
                fontSize,
                fontFamily,
                fontWeight,
                lineHeight,
            },
            maxWidth * canvasScale,
            canvasRef.current
        )
    }, [canvasRef, canvasScale, text, color, strokeColor, fontWeight, fontSize, fontFamily, lineHeight, maxWidth])

    if (!canvasRef.current || !text) {
        return null
    }

    if (caption) {
        const { start, end } = caption

        const captionExistOnFrame = (!start && !end) || (start! <= currentPlayheadTime && end! >= currentPlayheadTime)
        const isEditingCaption = draggingCaptionId === caption.id || contextMenuCaptionId === caption.id

        if (captionExistOnFrame || isEditingCaption) {
            return (
                <Layer
                    {...{
                        frame: canvasRef.current,
                        width: canvasRef.current.width / canvasScale,
                        height: canvasRef.current.height / canvasScale,
                        defaultX,
                        defaultY,
                        defaultRotation,
                        forceSelected,
                        resizeable,
                        rotateable,
                        moveable,
                        onMove,
                        caption,
                    }}
                />
            )
        }
    }

    return <></>
}

export default CreationToolsArtboardTextLayer
